<template>
  <div class="border border-gray-300 p-3 rounded-xl relative h-52 flex flex-col justify-between">
    <div
      class="flex items-center p-2 text-sm text-gray-800 rounded-xl justify-between"
      :class="alertBackgroundClass"
      role="alert"
    >
      <div class="flex items-center">
        <RefreshIcon v-if="isRunning" class="ml-1 spin" />
        <WarningSign v-else />
        <div class="ml-2">
          <span class="font-bold">{{ notificationTitle }}</span>
          <p class="text-xxs">{{ notification }}</p>
        </div>
      </div>
    </div>
    <div
      class="
        flex
        items-center
        p-2
        text-xxs text-gray-800
        rounded-xl
        mb-2
        justify-center
        align-center
      "
    >
      <SpeedIcon />
      <ArrowDownIcon class="mx-1 ml-2" />
      <span class="font-bold mr-1">{{ downloadSpeed }} Mbps</span>
      down
      <ArrowUpIcon class="mx-1 ml-4" />
      <span class="font-bold mr-1">{{ uploadSpeed }} Mbps</span>
      up
    </div>
    <div class="bottom-2 left-0 w-full h-12 px-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <div class="rounded-full w-12 h-12 flex justify-center items-center">
            <CloudCheckedIcon v-if="serverConnection" />
            <CloudCrossedIcon v-else />
          </div>
          <div class="flex items-center leading-5">
            <div class="device-type-label-regular font-medium device-type-label-preview">
              Server connection
              <br />
              <span class="device-label-regular font-medium">
                <span v-if="serverConnection">Connected</span>
                <span v-else>Not connected</span>
              </span>
            </div>
          </div>
        </div>
        <div v-if="!serverConnection" class="flex items-center h-6">
          <button
            @click="toggleTestHostsAndPortsModal"
            class="
              h-full
              px-2
              border
              text-x
              inline-flex
              items-center
              justify-center
              min-w-max
              font-medium
              rounded-md
              text-gray-500
              bg-white
            "
          >
            More info
          </button>
        </div>
      </div>
    </div>
  </div>

  <TestHostsAndPorts
    @toggleTestHostsAndPortsModal="toggleTestHostsAndPortsModal"
    @serverConnectionCheck="serverConnectionCheck"
    v-if="isTestHostsAndPorts"
  />
</template>
<script>
import { onMounted, ref, watch } from 'vue';
import SpeedTest from '@cloudflare/speedtest';
import WarningSign from '@/Components/Icons/DeviceAndSessionSetup/WarningSign.vue';
import CloudCrossedIcon from '@/Components/Icons/DeviceAndSessionSetup/CloudCrossedIcon.vue';
import CloudCheckedIcon from '@/Components/Icons/DeviceAndSessionSetup/CloudCheckedIcon.vue';
import ArrowDownIcon from '@/Components/Icons/DeviceAndSessionSetup/ArrowDownIcon.vue';
import ArrowUpIcon from '@/Components/Icons/DeviceAndSessionSetup/ArrowUpIcon.vue';
import SpeedIcon from '@/Components/Icons/DeviceAndSessionSetup/SpeedIcon.vue';
import TestHostsAndPorts from '@/Components/Modals/TestHostsAndPorts.vue';
import RefreshIcon from '@/Components/Icons/RefreshIcon.vue';
// import { APP_URL, TEST_IMAGE_CDN_URL, TEST_IMAGE_SIZE } from '@/Utils/vice-constants';

export default {
  name: 'TestConnectionSlot',
  components: {
    RefreshIcon,
    TestHostsAndPorts,
    SpeedIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    CloudCheckedIcon,
    CloudCrossedIcon,
    WarningSign,
  },
  computed: {
    alertBackgroundClass() {
      switch (this.notificationType) {
        case 'Warning':
          return 'header-warning';
        case 'Info':
          return 'header-info';
        default:
          return '';
      }
    },
  },
  setup() {
    const isRunning = ref(null);
    const uploadSpeed = ref(0);
    const downloadSpeed = ref(0);
    const ableStreams = ref(0);
    const ableParticipants = ref(0);
    const notification = ref('We are testing your connection...');
    const notificationTitle = ref('Estimation');
    const notificationType = ref('Info');
    const serverConnection = ref(null);
    const isTestHostsAndPorts = ref(false);
    const connectionLabels = ['Insufficient', 'Limited', 'Normal', 'Good'];

    onMounted(() => {
      runChecks();
    });

    const toggleTestHostsAndPortsModal = (show = false) => {
      isTestHostsAndPorts.value = show;
    };

    const runChecks = async () => {
      isRunning.value = true;
      const timeoutDuration = 35000;
      await Promise.all([
        withTimeout(checkServerConnection(), timeoutDuration),
        withTimeout(checkInternetSpeed(), timeoutDuration),
      ]);
      isRunning.value = false;
    };

    function withTimeout(promise, timeout) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          console.error('Connection test timed out');
          resolve();
        }, timeout);

        promise
          .then((result) => {
            clearTimeout(timer);
            resolve(result);
          })
          .catch((error) => {
            clearTimeout(timer);
            reject(error);
          });
      });
    }

    const checkServerConnection = async () => {
      const report = await TestHostsAndPorts.methods.shortCheckAllResources();
      const passedValues = report.map((check) => check.passed);
      const hasFailed = passedValues.some((value) => value === false);
      serverConnection.value = !hasFailed;
    };

    const checkInternetSpeed = () =>
      new Promise((resolve) => {
        const speedTest = new SpeedTest();

        speedTest.onResultsChange = (result) => {
          if (result.type === 'download') {
            downloadSpeed.value = (speedTest.results.getSummary()[result.type] / 1000000).toFixed(
              2,
            );
          }

          if (result.type === 'upload') {
            uploadSpeed.value = (speedTest.results.getSummary()[result.type] / 1000000).toFixed(2);
          }
          setNotification();
        };

        speedTest.onFinish = () => {
          setNotification();
          resolve();
        };
      });

    function setNotification() {
      const maxDevices = 5;
      const maxParticipants = 20;
      const connectionStatus = getInetSpeedLabel();
      ableStreams.value = getBandwidthAbility(uploadSpeed.value, 3, maxDevices);
      notification.value = `Up to ${ableStreams.value} outgoing streams with ${ableParticipants.value} participants`;
      ableParticipants.value = getBandwidthAbility(downloadSpeed.value, 2, maxParticipants);
      notificationTitle.value = `${connectionLabels[connectionStatus]} connection`;
    }

    const serverConnectionCheck = (passed) => {
      serverConnection.value = passed;
    };

    function getBandwidthAbility(speed, costOne, max) {
      const number = speed / costOne;
      if (number >= max) return max;

      return Math.floor(number);
    }

    function getInetSpeedLabel() {
      if (downloadSpeed.value < 10 || uploadSpeed.value < 5) return 0;
      if (downloadSpeed.value < 20 || uploadSpeed.value < 10) return 1;
      if (downloadSpeed.value > 40 && uploadSpeed.value > 20) return 3;
      if (downloadSpeed.value > 20 && uploadSpeed.value > 10) return 2;

      return 4;
    }

    watch(serverConnection, (newValue) => {
      if (newValue === false) {
        notificationType.value = 'Warning';
        notificationTitle.value = 'Session not possible';
        notification.value = 'No connections to our servers.';
      } else {
        notificationType.value = 'Info';
        notificationTitle.value = 'Good connection';
        notification.value = 'All good';
      }
    });

    watch(isRunning, (newValue) => {
      if (newValue === false && serverConnection.value === true) {
        const connectionStatus = getInetSpeedLabel();
        notificationTitle.value = `${connectionLabels[connectionStatus]} connection`;
        notification.value = `Up to ${ableStreams.value} outgoing streams with ${ableParticipants.value} participants`;
      }
    });

    return {
      isRunning,
      uploadSpeed,
      notification,
      downloadSpeed,
      notificationType,
      serverConnection,
      notificationTitle,
      isTestHostsAndPorts,
      serverConnectionCheck,
      toggleTestHostsAndPortsModal,
    };
  },
};
</script>
<style scoped>
.text-x {
  font-size: 12px;
}

.header-info {
  background-color: #eeeeee;
}

.header-warning {
  background-color: rgb(254, 243, 199);
  color: #881337;
}

.device-label-regular {
  color: #4b4b4b;
  font-size: 12px;
}

.device-type-label-preview {
  color: #881337;
  font-size: 16px;
}

.spin {
  animation: spin-animation 0.5s infinite reverse;
  display: inline-block;
}
</style>
